import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { PricingTargetRow } from "@shared-core/data/commercial-exceptions-types";
import {
  flagRateAtTarget,
  formatCmPct,
  marginQuantiles,
} from "@shared-core/domain/commercial-exceptions";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/** Compact p25–p75 band with median tick and target marker, on a fixed
 * -20%…95% CM axis so rows compare visually. Pure presentation. */
function MarginBand({
  row,
  proposedTarget,
}: {
  row: PricingTargetRow;
  proposedTarget: number | null;
}) {
  const q = marginQuantiles(row.recent_margins);
  if (!q) return <span className="text-xs text-muted-foreground">no data</span>;
  const min = -0.2;
  const max = 0.95;
  const x = (v: number) =>
    `${((Math.max(min, Math.min(max, v)) - min) / (max - min)) * 100}%`;
  return (
    <div className="relative h-6 w-full min-w-40" aria-hidden>
      <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
      <div
        className="absolute top-1.5 bottom-1.5 rounded bg-blue-200 dark:bg-blue-900"
        style={{ left: x(q.p25), width: `calc(${x(q.p75)} - ${x(q.p25)})` }}
        title={`p25 ${formatCmPct(q.p25)} · median ${formatCmPct(q.median)} · p75 ${formatCmPct(q.p75)}`}
      />
      <div
        className="absolute top-1 bottom-1 w-0.5 bg-blue-700 dark:bg-blue-300"
        style={{ left: x(q.median) }}
        title={`median ${formatCmPct(q.median)}`}
      />
      {proposedTarget != null && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-500"
          style={{ left: x(proposedTarget) }}
          title={`target ${formatCmPct(proposedTarget)}`}
        />
      )}
    </div>
  );
}

interface RowDraft {
  /** Percent-points string as typed, e.g. "25" for 25%. */
  value: string;
  saving: boolean;
}

export function SettingsPricingTargetsPage() {
  const adapter = useDataAdapter();
  const [rows, setRows] = useState<PricingTargetRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const policy = await adapter.getPricingTargetPolicy();
      setRows(policy.rows);
      setDrafts(
        Object.fromEntries(
          policy.rows.map((r) => [
            r.work_style,
            {
              value:
                r.target_cm_pct == null
                  ? ""
                  : (r.target_cm_pct * 100).toFixed(0),
              saving: false,
            },
          ]),
        ),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [adapter]);

  useEffect(() => {
    void load();
  }, [load]);

  const parsedTargets = useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const [family, draft] of Object.entries(drafts)) {
      const n = Number.parseFloat(draft.value);
      out[family] = Number.isFinite(n) ? n / 100 : null;
    }
    return out;
  }, [drafts]);

  const save = async (row: PricingTargetRow) => {
    const target = parsedTargets[row.work_style] ?? null;
    setDrafts((d) => ({
      ...d,
      [row.work_style]: { ...d[row.work_style], saving: true },
    }));
    try {
      // The save echoes the new live version (no sample data) — merge it
      // into the calibration row, keeping the margins band as-is.
      const saved = await adapter.savePricingTarget(row.work_style, target);
      setRows((rs) =>
        rs
          ? rs.map((r) =>
              r.work_style === saved.work_style ? { ...r, ...saved } : r,
            )
          : rs,
      );
      toast.success(`${row.label} target saved`);
    } catch {
      toast.error("Could not save the target — try again");
    } finally {
      setDrafts((d) => ({
        ...d,
        [row.work_style]: { ...d[row.work_style], saving: false },
      }));
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Loading pricing targets…
      </div>
    );
  }
  if (error || !rows) {
    return (
      <div className="p-6 space-y-2">
        <p className="text-sm font-medium">Could not load pricing targets.</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <Link
          to={paths.commercialExceptions()}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Commercial exceptions
        </Link>
        <h1 className="text-2xl font-semibold mt-1">Pricing targets</h1>
        <p className="text-sm text-muted-foreground">
          Target contribution margin by work style. A quote below its resolved
          target raises a <span className="font-medium">Below target</span>{" "}
          exception. The band shows the actual CM spread (p25–p75, median tick)
          over the last 90 days; the red line is the target you are setting, and
          “would flag” is the share of recent quotes that would have tripped it.
        </p>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface text-left">
              <th className="px-3 py-2 font-medium">Work style</th>
              <th className="px-3 py-2 font-medium">Target CM %</th>
              <th className="px-3 py-2 font-medium">Recent CM (90d)</th>
              <th className="px-3 py-2 font-medium text-right">Would flag</th>
              <th className="px-3 py-2 font-medium">Last changed</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = drafts[row.work_style];
              const proposed = parsedTargets[row.work_style] ?? null;
              const rate = flagRateAtTarget(row.recent_margins, proposed);
              const dirty =
                (row.target_cm_pct == null
                  ? ""
                  : (row.target_cm_pct * 100).toFixed(0)) !==
                (draft?.value ?? "");
              // 'other' is the unresolved bucket — a calibration health
              // measure, never a configurable target (the API rejects it).
              const readOnly = row.work_style === "other";
              return (
                <tr
                  key={row.work_style}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.label}</div>
                  </td>
                  <td className="px-3 py-2">
                    {readOnly ? (
                      <span className="text-muted-foreground">no target</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input
                          value={draft?.value ?? ""}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [row.work_style]: {
                                ...d[row.work_style],
                                value: e.target.value,
                              },
                            }))
                          }
                          inputMode="decimal"
                          placeholder="—"
                          className="w-20"
                          aria-label={`${row.label} target CM percent`}
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 w-56">
                    <MarginBand row={row} proposedTarget={proposed} />
                    <div className="text-xs text-muted-foreground">
                      {row.recent_margins.length} quotes
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {rate == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span
                        className={
                          rate > 0.35
                            ? "text-red-600 dark:text-red-400 font-medium"
                            : "font-medium"
                        }
                      >
                        {Math.round(rate * 100)}%
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.updated_by
                      ? `${row.updated_by} · ${formatDateOrDash(row.updated_at)}`
                      : "never"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!dirty || draft?.saving}
                        onClick={() => void save(row)}
                      >
                        {draft?.saving ? "Saving…" : "Save"}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        A target flagging much more than about a third of recent quotes will
        flood the board — tighten the pricing model or the target, not the
        reviewers. Per-customer overrides are managed with the customer’s price
        group (phase 2).
      </p>
    </div>
  );
}
